import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Download } from "lucide-react";
import { API } from "@/lib/api";

export default function DPT3Wizard() {
  const [fy, setFy] = useState("2025-26");
  const [d, setD] = useState({ fy: "2025-26", cin: "", return_type: "annual",
    outstanding_receipt_of_money: 0, outstanding_loans_directors: 0,
    outstanding_loans_shareholders: 0, outstanding_body_corporate: 0,
    outstanding_from_banks: 0, outstanding_from_govt: 0, outstanding_debentures: 0,
    outstanding_bonds: 0, outstanding_others: 0, total_amount: 0,
    net_worth_last_audited: 0, credit_rating_agency: "", credit_rating: "" });
  useEffect(() => { api.get("/mca/dpt-3", { params: { fy } }).then((r) => setD({ ...d, ...r.data, fy })); /* eslint-disable-next-line */ }, [fy]);
  useEffect(() => {
    const t = ["outstanding_receipt_of_money","outstanding_loans_directors","outstanding_loans_shareholders","outstanding_body_corporate","outstanding_from_banks","outstanding_from_govt","outstanding_debentures","outstanding_bonds","outstanding_others"].reduce((s, k) => s + Number(d[k] || 0), 0);
    if (t !== d.total_amount) setD((p) => ({ ...p, total_amount: t }));
    // eslint-disable-next-line
  }, [d.outstanding_receipt_of_money, d.outstanding_loans_directors, d.outstanding_loans_shareholders, d.outstanding_body_corporate, d.outstanding_from_banks, d.outstanding_from_govt, d.outstanding_debentures, d.outstanding_bonds, d.outstanding_others]);
  const setF = (k, v) => setD({ ...d, [k]: v });
  const save = async () => { try { await api.post("/mca/dpt-3", d); toast.success("DPT-3 draft saved"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const exportJson = async () => {
    const res = await fetch(`${API}/mca/dpt-3.json?fy=${fy}`, { credentials: "include" });
    if (!res.ok) return toast.error("Save first");
    const blob = await res.blob(); const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `DPT-3_${fy}.json`; a.click(); URL.revokeObjectURL(u);
  };
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dpt3-page">
      <PageHeader eyebrow="Corporate Compliance" title="DPT-3 · Return of Deposits"
        description="Companies Act 2013 · Rule 16 & 16A. Filed by 30 June annually with particulars of every outstanding money not treated as deposit." />
      <Card className="p-4 rounded-sm flex items-end gap-3 flex-wrap">
        <div><Label>Financial Year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-sm mt-1 w-32" data-testid="dpt3-fy" /></div>
        <div><Label>CIN</Label><Input value={d.cin || ""} onChange={(e) => setF("cin", e.target.value.toUpperCase())} className="rounded-sm mt-1 font-mono w-64" data-testid="dpt3-cin" /></div>
        <div><Label>Return Type</Label>
          <Select value={d.return_type} onValueChange={(v) => setF("return_type", v)}>
            <SelectTrigger className="rounded-sm mt-1 w-44" data-testid="dpt3-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="annual">Annual Return (Rule 16)</SelectItem>
              <SelectItem value="onetime">One-Time Return (Rule 16A)</SelectItem>
              <SelectItem value="deposits">Deposits Only</SelectItem>
              <SelectItem value="both">Both Deposits + Non-deposit Receipts</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button variant="outline" className="rounded-sm gap-1.5" onClick={save} data-testid="dpt3-save"><Save className="w-4 h-4" /> Save</Button>
        <Button className="rounded-sm bg-accent text-white gap-1.5" onClick={exportJson} data-testid="dpt3-export"><Download className="w-4 h-4" /> Export JSON</Button>
      </Card>
      <Tabs defaultValue="particulars">
        <TabsList className="rounded-sm">
          <TabsTrigger value="particulars" className="rounded-sm">Particulars of Outstanding</TabsTrigger>
          <TabsTrigger value="credit" className="rounded-sm">Net Worth & Credit Rating</TabsTrigger>
        </TabsList>
        <TabsContent value="particulars" className="mt-4"><Card className="p-5 rounded-sm space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            {[
              ["outstanding_receipt_of_money", "(a) Receipt of money not treated as deposit"],
              ["outstanding_loans_directors", "(b) From directors / their relatives"],
              ["outstanding_loans_shareholders", "(c) From shareholders (private co.)"],
              ["outstanding_body_corporate", "(d) From other body corporate"],
              ["outstanding_from_banks", "(e) From banking company"],
              ["outstanding_from_govt", "(f) From Government / statutory authority"],
              ["outstanding_debentures", "(g) Convertible / non-convertible debentures"],
              ["outstanding_bonds", "(h) Bonds"],
              ["outstanding_others", "(i) Any other amount"],
            ].map(([k, label]) => (
              <div key={k}><Label>{label} (₹)</Label>
                <Input type="number" value={d[k]} onChange={(e) => setF(k, Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid={`dpt3-${k}`} />
              </div>
            ))}
          </div>
          <div className="border-t pt-3 flex items-center justify-between bg-muted/40 -m-5 mt-3 px-5 py-3 rounded-b-sm">
            <div className="text-sm font-semibold">Total Amount Outstanding</div>
            <div className="text-xl metric-value">₹{Number(d.total_amount).toLocaleString("en-IN")}</div>
          </div>
        </Card></TabsContent>
        <TabsContent value="credit" className="mt-4"><Card className="p-5 rounded-sm grid md:grid-cols-3 gap-3">
          <div><Label>Net Worth (last audited) (₹)</Label><Input type="number" value={d.net_worth_last_audited} onChange={(e) => setF("net_worth_last_audited", Number(e.target.value))} className="rounded-sm mt-1 text-right tabular-nums" data-testid="dpt3-nw" /></div>
          <div><Label>Credit Rating Agency</Label><Input value={d.credit_rating_agency || ""} onChange={(e) => setF("credit_rating_agency", e.target.value)} className="rounded-sm mt-1" data-testid="dpt3-cra" placeholder="CRISIL / ICRA" /></div>
          <div><Label>Credit Rating</Label><Input value={d.credit_rating || ""} onChange={(e) => setF("credit_rating", e.target.value)} className="rounded-sm mt-1 font-mono" data-testid="dpt3-cr" placeholder="AA+" /></div>
        </Card></TabsContent>
      </Tabs>
    </div>
  );
}
